import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import MetricCard from '../components/shared/MetricCard';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import { formatMoney } from '../utils/formatters';

export default function OrgSummaryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getShopsSummary();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading) return <LoadingSpinner text="Loading organization summary..." />;
  if (error) return <ErrorState message={error} onRetry={fetchSummary} />;

  const shops = data?.shops || [];
  const orgTotalCredits = data?.org_total_credits_usd || 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-800 mb-1">Organization Summary</h1>
        <p className="text-sm text-gray-400">Cross-shop comparison of outstanding issues and credits</p>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap">
        <MetricCard
          label="Total Shops"
          value={shops.length.toString()}
          icon={'\uD83C\uDFE2'}
        />
        <MetricCard
          label="Org-Wide Outstanding Credits"
          value={formatMoney(orgTotalCredits)}
          accent="#C62828"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Shop</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Outstanding Credits</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Returns Count</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Missing POs</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost Variance</th>
            </tr>
          </thead>
          <tbody>
            {shops.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                  No shop data available
                </td>
              </tr>
            ) : (
              shops.map((shop, i) => (
                <tr key={shop.shop_id || shop.id || i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{shop.shop_name || shop.name}</td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{shop.shop_id || shop.id}</td>
                  <td className="px-4 py-3 text-right font-bold text-red-700">
                    {formatMoney(shop.credits_total_usd || shop.credits_total / 100 || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">{shop.returns_count ?? shop.credit_count ?? '\u2014'}</td>
                  <td className="px-4 py-3 text-right">{shop.missing_po_count ?? '\u2014'}</td>
                  <td className="px-4 py-3 text-right">
                    {shop.cost_variance_usd != null
                      ? formatMoney(shop.cost_variance_usd)
                      : '\u2014'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {shops.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="px-4 py-3 font-bold" colSpan={2}>Organization Total</td>
                <td className="px-4 py-3 text-right font-extrabold text-red-800">
                  {formatMoney(orgTotalCredits)}
                </td>
                <td className="px-4 py-3 text-right font-bold">
                  {shops.reduce((s, sh) => s + (sh.returns_count || sh.credit_count || 0), 0)}
                </td>
                <td className="px-4 py-3 text-right font-bold">
                  {shops.reduce((s, sh) => s + (sh.missing_po_count || 0), 0) || '\u2014'}
                </td>
                <td className="px-4 py-3 text-right font-bold">
                  {shops.some(sh => sh.cost_variance_usd != null)
                    ? formatMoney(shops.reduce((s, sh) => s + (sh.cost_variance_usd || 0), 0))
                    : '\u2014'}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
